import React, { useEffect, useState } from "react";
import { ArrowLeft, Download, Loader2, X } from "lucide-react";

interface Props {
  url?: string;
  base64Data?: { data: string; mimeType: string };
  title: string;
  onClose: () => void;
  hasPrevious?: boolean;
  navigatingBack?: boolean;
  onPrevious?: () => void;
}

export const PdfViewerModal: React.FC<Props> = ({
  url,
  base64Data,
  title,
  onClose,
  hasPrevious,
  navigatingBack,
  onPrevious,
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setLoading(true);
    setError(null);
    setBlobUrl(null);

    const cleanup = () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };

    if (base64Data) {
      try {
        const byteStr = atob(base64Data.data);
        const bytes = new Uint8Array(byteStr.length);
        for (let i = 0; i < byteStr.length; i++) {
          bytes[i] = byteStr.charCodeAt(i);
        }
        objectUrl = URL.createObjectURL(
          new Blob([bytes], { type: base64Data.mimeType }),
        );
        if (!cancelled) {
          setBlobUrl(objectUrl);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message);
          setLoading(false);
        }
      }
    } else if (url) {
      fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.blob();
        })
        .then((blob) => {
          if (cancelled) return;
          objectUrl = URL.createObjectURL(
            new Blob([blob], { type: "application/pdf" }),
          );
          setBlobUrl(objectUrl);
          setLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          setError((err as Error).message);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }

    return cleanup;
  }, [url, base64Data]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl flex flex-col w-[95vw] h-[90vh]">
        <div className="flex items-center justify-between px-5 py-3 border-b shrink-0">
          <h2
            className="text-sm font-semibold text-gray-800 truncate max-w-lg"
            title={title}
          >
            {title}
          </h2>
          <div className="flex items-center gap-2 ml-4 shrink-0">
            <a
              href={blobUrl ?? url ?? "#"}
              download={title}
              onClick={(e) => {
                if (!blobUrl && !url) e.preventDefault();
              }}
              className={`inline-flex items-center gap-1 px-3 py-1 text-xs border rounded ${
                blobUrl || url
                  ? "text-blue-600 border-blue-400 hover:bg-blue-50"
                  : "text-gray-300 border-gray-200 pointer-events-none"
              }`}
            >
              <Download className="w-3 h-3" />
              Download
            </a>
            <button
              onClick={onPrevious}
              disabled={!hasPrevious || navigatingBack}
              className="inline-flex items-center gap-1 px-3 py-1 text-xs border rounded disabled:opacity-40 disabled:cursor-not-allowed text-gray-600 border-gray-300 hover:bg-gray-50 disabled:hover:bg-transparent"
              title={hasPrevious ? "Go to previous PDF" : "No previous PDF"}
            >
              {navigatingBack ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <ArrowLeft className="w-3.5 h-3.5" />
              )}
              Previous
            </button>
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          {(loading || navigatingBack) && (
            <div className="flex items-center justify-center h-full gap-2 text-gray-500 text-sm">
              <Loader2 className="w-5 h-5 animate-spin" />
              {navigatingBack ? "Loading previous PDF…" : "Loading PDF…"}
            </div>
          )}
          {error && !navigatingBack && (
            <div className="flex items-center justify-center h-full text-red-500 text-sm">
              Failed to load: {error}
            </div>
          )}
          {!loading && !error && !navigatingBack && blobUrl && (
            <iframe
              src={blobUrl}
              title={title}
              className="w-full h-full border-0"
            />
          )}
        </div>
      </div>
    </div>
  );
};
